
import Vue, { PropType } from 'vue';
import Modal from '@/components/modal/Modal.vue';
import { AssignRuleForm, RuleTemplate } from '@/interfaces/rules';
import { required } from 'vuelidate/lib/validators';
import { Validation } from 'vuelidate/vuelidate';
import { RoomZoneDeviceTypes } from '@/interfaces/locations';
import { SelectMenu, SelectMultipleMenu } from '@/interfaces/global';
import { DeviceType } from '@/interfaces/types';

interface RuleForm {
	rule: number | null;
	movementSensor: number[];
	lightLevelSensor: number | null;
}

/*
	This section is picked up by vue-i18n. DO NOT REMOVE.

	// Status messages
	this.$t('No movement sensors');
	this.$t('No light level sensors');
*/

export default Vue.extend({
	components: {
		Modal,
	},

	data() {
		return {
			assignRuleForm: {
				rule: null,
				movementSensor: [],
				lightLevelSensor: null,
			},
		} as {
			assignRuleForm: RuleForm;
		};
	},

	validations: {
		assignRuleForm: {
			rule: { required },
			movementSensor: {},
			lightLevelSensor: {},
		},
	},

	props: {
		rules: { type: Array as PropType<RuleTemplate[]> },
		roomSensorsWithMovement: { type: Object as PropType<RoomZoneDeviceTypes> },
	},

	computed: {
		ruleOptions(): SelectMenu[] {
			return this.rules.map((rule) => {
				return {
					value: rule.id,
					text: rule.name,
				};
			});
		},

		movementOptions(): SelectMultipleMenu[] {
			return this.movementOrLightLevelOptions(['dt_movement_count'], 'No movement sensors');
		},

		lightLevelOptions(): SelectMultipleMenu[] {
			return this.movementOrLightLevelOptions(
				['dt_light_lx', 'dt_light_pct'],
				'No light level sensors'
			);
		},
	},

	methods: {
		closeModal(): void {
			this.$bvModal.hide('modal-assign-rule');
		},

		confirmModal(): void {
			this.$v.assignRuleForm.$touch();

			if (this.$v.assignRuleForm.$anyError) {
				return;
			}

			const finalRuleForm: AssignRuleForm = {
				rule: this.assignRuleForm.rule,
				movement_devices:
					this.assignRuleForm.movementSensor && this.assignRuleForm.movementSensor.length
						? this.assignRuleForm.movementSensor
						: [],
				lighting_device: this.assignRuleForm.lightLevelSensor
					? this.assignRuleForm.lightLevelSensor
					: null,
			};

			this.$emit('assignRule', finalRuleForm);
		},

		resetModal(): void {
			this.assignRuleForm = {
				rule: null,
				lightLevelSensor: null,
				movementSensor: [],
			};
			this.$v.assignRuleForm.$reset();
		},

		validateState(validation: Validation): boolean | null {
			const { $dirty, $error }: { $dirty: boolean; $error: boolean } = validation;
			return $dirty ? !$error : null;
		},

		movementOrLightLevelOptions(features: string[], notFoundMessage: string): SelectMultipleMenu[] {
			const options: SelectMultipleMenu[] = [];
			let withPirTypes: DeviceType[] = this.roomSensorsWithMovement.types
				.filter((type: DeviceType) => type.features.some((r) => features.includes(r)))
				.map((type: DeviceType) => type);

			for (const roomSensorsWithPirKey of Object.keys(this.roomSensorsWithMovement.devices)) {
				options.push({
					label: `${this.$t('Zone')}: ${roomSensorsWithPirKey}`,
					options: this.roomSensorsWithMovement.devices[roomSensorsWithPirKey].some((device) =>
						withPirTypes?.find((withPirTypes) => withPirTypes['id'] === device.fk_device_type)
					)
						? [
								...this.roomSensorsWithMovement.devices[roomSensorsWithPirKey]
									.filter((device) =>
										withPirTypes?.find(
											(withPirTypes) => withPirTypes['id'] === device.fk_device_type
										)
									)
									.map((device) => {
										return {
											value: device.id,
											text: `${
												withPirTypes?.find(
													(withPirTypes) => withPirTypes['id'] === device.fk_device_type
												)?.name
											}: ${device.name}`,
										};
									}),
						  ]
						: [{ value: null, text: this.$t(notFoundMessage), disabled: true }],
				});
			}
			return options;
		},
	},
});
